import { createFileRoute } from '@tanstack/react-router'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Save, Mail, Phone, MapPin, Loader2 } from 'lucide-react'
import { Header } from '@/components/layout/header'
import { Main } from '@/components/layout/main'
import { TopNav } from '@/components/layout/top-nav'
import { ProfileDropdown } from '@/components/profile-dropdown'
import { Search as SearchComponent } from '@/components/search'
import { ThemeSwitch } from '@/components/theme-switch'
import { RichTextEditor } from '@/components/rich-text-editor'
import { MediaPicker } from '@/components/media/media-picker'
import { useSettings } from '@/hooks/use-settings'
import { useEffect, useState } from 'react'
import { getMediaUrl } from '@/utils/url-utils'

const topNav = [
  { title: 'Anasayfa', href: '/', isActive: false },
]

function SiteAyalariPage() {
  const { settings, isLoading, updateSettings, isUpdating, initializeSettings, isInitializing } = useSettings()
  const [formData, setFormData] = useState<any>({})
  const [activeTab, setActiveTab] = useState("genel")
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    key: 'maintenance_mode' | 'under_construction';
    value: boolean;
    title: string;
    description: string;
  } | null>(null)

  const [pickerConfig, setPickerConfig] = useState<{ isOpen: boolean, key: string, title: string }>({ isOpen: false, key: '', title: '' })

  // Initialize form when data loads
  useEffect(() => {
    if (settings && Object.keys(settings).length > 0) {
      setFormData(JSON.parse(JSON.stringify(settings))) // Deep copy
    }
  }, [settings])

  const TAB_KEY_MAP: Record<string, string[]> = {
    genel: ['site_name', 'site_description', 'default_language', 'about_text', 'maintenance_mode', 'under_construction'],
    iletisim: ['company_info', 'address', 'contact_phone', 'contact_email', 'whatsapp_number', 'working_hours', 'map_embed_code', 'secondary_contact'],
    seo: ['seo_settings', 'analytics', 'advanced_tracking'],
    sosyal: ['social_media'],
    gorunum: ['appearance', 'custom_css'],
    eposta: ['email_settings', 'smtp_settings'],
    kvkk: ['cookie_settings']
  }

  const handleSave = async () => {
    const activeKeys = TAB_KEY_MAP[activeTab] || []
    const diff: any = {}

    for (const key of activeKeys) {
      if (formData[key] !== undefined && JSON.stringify(formData[key]) !== JSON.stringify(settings?.[key])) {
        diff[key] = formData[key]
      }
    }

    if (Object.keys(diff).length > 0) {
      await updateSettings(diff)
    }
  }

  const handleChange = (key: string, value: any) => {
    setFormData((prev: any) => {
      const updated = { ...prev, [key]: value };

      // Mutual Exclusivity Logic for regular handleChange (as fallback if triggered through other means)
      if (key === 'maintenance_mode' && value === true) {
        updated.under_construction = false;
      }
      if (key === 'under_construction' && value === true) {
        updated.maintenance_mode = false;
      }

      return updated;
    })
  }

  const handleModeToggleClick = (key: 'maintenance_mode' | 'under_construction', value: boolean) => {
    const isMaintenance = key === 'maintenance_mode';
    setConfirmDialog({
      isOpen: true,
      key,
      value,
      title: isMaintenance ? 'Bakım Modu Onayı' : 'Yapım Aşamasında Onayı',
      description: value
        ? `Bu modu aktif ettiğinizde normal ziyaretçiler siteye erişemeyecek. Özel bir ekran görecekler. Onaylıyor musunuz?`
        : `Bu modu kapatarak siteyi tekrar tamamen genel erişime açıyorsunuz. Onaylıyor musunuz?`
    });
  }

  const handleConfirmModeToggle = async () => {
    if (!confirmDialog) return;
    const { key, value } = confirmDialog;

    // Update local form state including exclusivity
    setFormData((prev: any) => {
      const updated = { ...prev, [key]: value };
      if (key === 'maintenance_mode' && value === true) updated.under_construction = false;
      if (key === 'under_construction' && value === true) updated.maintenance_mode = false;
      return updated;
    });

    // Prepare exactly what we want to patch to the API right now
    const apiPayload: any = { [key]: value };
    if (key === 'maintenance_mode' && value === true) apiPayload.under_construction = false;
    if (key === 'under_construction' && value === true) apiPayload.maintenance_mode = false;

    // Trigger instant backend patch
    await updateSettings(apiPayload);

    setConfirmDialog(null);
  }

  const handleNestedChange = (group: string, key: string, value: any) => {
    setFormData((prev: any) => ({
      ...prev,
      [group]: {
        ...(prev[group] || {}),
        [key]: value
      }
    }))
  }

  if (isLoading) {
    return (
      <>
        <Header>
          <TopNav links={topNav} />
          <div className="ml-auto flex items-center gap-2">
            <SearchComponent />
            <ThemeSwitch />
            <ProfileDropdown />
          </div>
        </Header>
        <Main>
          <div className="flex h-[80vh] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </Main>
      </>
    )
  }

  if (!settings || Object.keys(settings).length === 0 || !formData.site_name) {
    return (
      <Main className="h-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 mt-20">
          <h2 className="text-xl font-semibold">Ayarlar Bulunamadı</h2>
          <p className="text-muted-foreground">Veritabanında henüz bir sistem ayarı yapılandırılmamış.</p>
          <Button onClick={() => initializeSettings()} disabled={isInitializing}>
            {isInitializing ? 'Oluşturuluyor...' : 'Varsayılan Ayarları Yükle'}
          </Button>
        </div>
      </Main>
    )
  }

  return (
    <>
      <Header>
        <TopNav links={topNav} />
        <div className="ml-auto flex items-center gap-2">
          <SearchComponent />
          <ThemeSwitch />
          <ProfileDropdown />
        </div>
      </Header>

      <Main>
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Site Genel Ayarları</h1>
            <p className="text-sm text-muted-foreground">Web sitesi genel ayarlarını ve yapılandırmasını yönetin</p>
          </div>
          <Button onClick={handleSave} disabled={isUpdating}>
            <Save className="mr-2 h-4 w-4" />
            {isUpdating ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="overflow-x-auto pb-1">
            <TabsList className="min-w-max">
              <TabsTrigger value="genel">Genel Bilgiler</TabsTrigger>
              <TabsTrigger value="iletisim">Kurumsal & İletişim</TabsTrigger>
              <TabsTrigger value="seo">SEO & İzleme</TabsTrigger>
              <TabsTrigger value="sosyal">Sosyal Medya</TabsTrigger>
              <TabsTrigger value="gorunum">Görünüm & Medya</TabsTrigger>
              <TabsTrigger value="eposta">E-posta & SMTP</TabsTrigger>
              <TabsTrigger value="kvkk">KVKK & Çerezler</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="genel" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Site Genel Bilgileri</CardTitle>
                <CardDescription>Web sitesinin temel bilgileri</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="site-title">Site Başlığı</Label>
                    <Input id="site-title" value={formData.site_name || ''} onChange={e => handleChange('site_name', e.target.value)} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="site-description">Site Açıklaması</Label>
                    <Input id="site-description" value={formData.site_description || ''} onChange={e => handleChange('site_description', e.target.value)} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="language">Varsayılan Dil</Label>
                    <Select value={formData.default_language} onValueChange={(val) => handleChange('default_language', val)}>
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="tr">Türkçe</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="about-us">Hakkımızda Metni</Label>
                  <RichTextEditor
                    value={formData.about_text || ''}
                    onChange={(val) => handleChange('about_text', val)}
                  />
                </div>
                <div className="flex items-center space-x-2 pt-2">
                  <Switch id="maintenance-mode" checked={formData.maintenance_mode} onCheckedChange={(val) => handleModeToggleClick('maintenance_mode', val)} />
                  <Label htmlFor="maintenance-mode">Bakım Modu</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch id="under-construction" checked={formData.under_construction} onCheckedChange={(val) => handleModeToggleClick('under_construction', val)} />
                  <Label htmlFor="under-construction">Yapım Aşamasında</Label>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="iletisim" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Kurumsal Bilgiler</CardTitle>
                <CardDescription>Resmi kurum bilgileri</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="company-title">Firma Resmî Unvanı</Label>
                    <Input id="company-title" value={formData.company_info?.company_title || ''} onChange={e => handleNestedChange('company_info', 'company_title', e.target.value)} className="h-9" placeholder="Örn: SKG Metal San. ve Tic. A.Ş." />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mersis">Mersis No</Label>
                    <Input id="mersis" value={formData.company_info?.mersis_no || ''} onChange={e => handleNestedChange('company_info', 'mersis_no', e.target.value)} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tax-office">Vergi Dairesi</Label>
                    <Input id="tax-office" value={formData.company_info?.tax_office || ''} onChange={e => handleNestedChange('company_info', 'tax_office', e.target.value)} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tax-number">Vergi Numarası</Label>
                    <Input id="tax-number" value={formData.company_info?.tax_number || ''} onChange={e => handleNestedChange('company_info', 'tax_number', e.target.value)} className="h-9" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>İletişim Bilgileri</CardTitle>
                <CardDescription>Şirket iletişim bilgileri</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="address">
                      <MapPin className="inline h-4 w-4 mr-2" />
                      Adres
                    </Label>
                    <Input id="address" value={formData.address || ''} onChange={e => handleChange('address', e.target.value)} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="working-hours">Çalışma Saatleri</Label>
                    <Input id="working-hours" value={formData.working_hours || ''} onChange={e => handleChange('working_hours', e.target.value)} className="h-9" />
                  </div>

                  {/* Telefonlar */}
                  <div className="space-y-2">
                    <Label htmlFor="phone">
                      <Phone className="inline h-4 w-4 mr-2" />
                      Ana Telefon
                    </Label>
                    <Input id="phone" value={formData.contact_phone || ''} onChange={e => handleChange('contact_phone', e.target.value)} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone2">İkincil Telefon</Label>
                    <Input id="phone2" value={formData.secondary_contact?.phone2 || ''} onChange={e => handleNestedChange('secondary_contact', 'phone2', e.target.value)} className="h-9" />
                  </div>

                  {/* E-postalar */}
                  <div className="space-y-2">
                    <Label htmlFor="email">
                      <Mail className="inline h-4 w-4 mr-2" />
                      Ana E-posta
                    </Label>
                    <Input id="email" value={formData.contact_email || ''} onChange={e => handleChange('contact_email', e.target.value)} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email2">İkincil E-posta</Label>
                    <Input id="email2" value={formData.secondary_contact?.email2 || ''} onChange={e => handleNestedChange('secondary_contact', 'email2', e.target.value)} className="h-9" />
                  </div>

                  {/* Diğer */}
                  <div className="space-y-2">
                    <Label htmlFor="whatsapp">WhatsApp Numarası</Label>
                    <Input id="whatsapp" value={formData.whatsapp_number || ''} onChange={e => handleChange('whatsapp_number', e.target.value)} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fax">Faks</Label>
                    <Input id="fax" value={formData.secondary_contact?.fax || ''} onChange={e => handleNestedChange('secondary_contact', 'fax', e.target.value)} className="h-9" />
                  </div>
                </div>

                <div className="space-y-2 pt-2">
                  <Label htmlFor="map-embed">Harita Embed Kodu</Label>
                  <Textarea
                    id="map-embed"
                    rows={3}
                    placeholder="Google Maps iframe..."
                    value={formData.map_embed_code || ''}
                    onChange={e => handleChange('map_embed_code', e.target.value)}
                    className="resize-none"
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="seo" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Temel SEO Ayarları</CardTitle>
                <CardDescription>Arama motoru görünürlüğünüzü yönetin (Ana Sayfa İçin)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="space-y-2">
                  <Label htmlFor="meta-title">Meta Başlık</Label>
                  <Input id="meta-title" value={formData.seo_settings?.meta_title || ''} onChange={e => handleNestedChange('seo_settings', 'meta_title', e.target.value)} className="h-9" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meta-description">Meta Açıklaması</Label>
                  <Textarea
                    id="meta-description"
                    rows={3}
                    value={formData.seo_settings?.meta_description || ''}
                    onChange={e => handleNestedChange('seo_settings', 'meta_description', e.target.value)}
                    className="resize-none"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="meta-keywords">Meta Anahtar Kelimeler</Label>
                  <Input id="meta-keywords" value={formData.seo_settings?.meta_keywords || ''} onChange={e => handleNestedChange('seo_settings', 'meta_keywords', e.target.value)} className="h-9" placeholder="metal, sanayi, üretim..." />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div className="flex items-center space-x-2 border p-3 rounded">
                    <Switch id="robots-txt" checked={formData.seo_settings?.generate_robots_txt || false} onCheckedChange={(val) => handleNestedChange('seo_settings', 'generate_robots_txt', val)} />
                    <Label htmlFor="robots-txt">robots.txt Oluştur & İzin Ver</Label>
                  </div>
                  <div className="flex items-center space-x-2 border p-3 rounded">
                    <Switch id="sitemap" checked={formData.seo_settings?.generate_sitemap || false} onCheckedChange={(val) => handleNestedChange('seo_settings', 'generate_sitemap', val)} />
                    <Label htmlFor="sitemap">Sitemap.xml Aktif Et</Label>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>İzleme & Analitik Kodları</CardTitle>
                <CardDescription>Ziyaretçi trafiğini izlemek ve dönüşümleri ölçmek için servis ID'lerini girin</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="google-analytics">Google Analytics Kodu</Label>
                    <Input id="google-analytics" placeholder="GA-XXXXXXXXX-X" value={formData.analytics?.google_analytics_id || ''} onChange={e => handleNestedChange('analytics', 'google_analytics_id', e.target.value)} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="google-tag-manager">Google Tag Manager</Label>
                    <Input id="google-tag-manager" placeholder="GTM-XXXXXXX" value={formData.analytics?.google_tag_manager_id || ''} onChange={e => handleNestedChange('analytics', 'google_tag_manager_id', e.target.value)} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gsc-verification">Search Console Doğrulama Puanı</Label>
                    <Input id="gsc-verification" placeholder="Bkz: google-site-verification" value={formData.advanced_tracking?.google_site_verification || ''} onChange={e => handleNestedChange('advanced_tracking', 'google_site_verification', e.target.value)} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="facebook-pixel">Facebook Pixel ID</Label>
                    <Input id="facebook-pixel" placeholder="Örn: 123456789012345" value={formData.advanced_tracking?.facebook_pixel_id || ''} onChange={e => handleNestedChange('advanced_tracking', 'facebook_pixel_id', e.target.value)} className="h-9" />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="yandex-metrica">Yandex Metrica ID</Label>
                    <Input id="yandex-metrica" placeholder="Örn: 98765432" value={formData.advanced_tracking?.yandex_metrica_id || ''} onChange={e => handleNestedChange('advanced_tracking', 'yandex_metrica_id', e.target.value)} className="h-9" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="sosyal" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Sosyal Medya Hesapları</CardTitle>
                <CardDescription>Sosyal medya bağlantıları</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="facebook">Facebook</Label>
                    <Input id="facebook" placeholder="https://facebook.com/skgmetal" value={formData.social_media?.facebook || ''} onChange={e => handleNestedChange('social_media', 'facebook', e.target.value)} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="twitter">Twitter</Label>
                    <Input id="twitter" placeholder="https://twitter.com/skgmetal" value={formData.social_media?.twitter || ''} onChange={e => handleNestedChange('social_media', 'twitter', e.target.value)} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="instagram">Instagram</Label>
                    <Input id="instagram" placeholder="https://instagram.com/skgmetal" value={formData.social_media?.instagram || ''} onChange={e => handleNestedChange('social_media', 'instagram', e.target.value)} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="linkedin">LinkedIn</Label>
                    <Input id="linkedin" placeholder="https://linkedin.com/company/skgmetal" value={formData.social_media?.linkedin || ''} onChange={e => handleNestedChange('social_media', 'linkedin', e.target.value)} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="kariyer-net">Kariyer.net</Label>
                    <Input id="kariyer-net" placeholder="https://www.kariyer.net/firma-profili/skg-metal" value={formData.social_media?.kariyerNet || ''} onChange={e => handleNestedChange('social_media', 'kariyerNet', e.target.value)} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="youtube">YouTube</Label>
                    <Input id="youtube" placeholder="https://youtube.com/@skgmetal" value={formData.social_media?.youtube || ''} onChange={e => handleNestedChange('social_media', 'youtube', e.target.value)} className="h-9" />
                  </div>
                </div>
                <div className="flex items-center space-x-2 pt-2">
                  <Switch id="show-social-icons" checked={formData.social_media?.show_icons || false} onCheckedChange={(val) => handleNestedChange('social_media', 'show_icons', val)} />
                  <Label htmlFor="show-social-icons">Sosyal Medya İkonlarını Göster</Label>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="gorunum" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Genel Görünüm & Medya</CardTitle>
                <CardDescription>Sitenizin temel kurumsal logo tasarımlarını ve ana rengini belirleyin</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 text-sm">

                {/* Main Logo Upload */}
                <div className="space-y-3">
                  <Label>Ana Logo (Header & Genel Kullanım)</Label>
                  <div className="flex items-center gap-4">
                    {formData.appearance?.logo_url && (
                      <div className="w-32 h-16 border rounded bg-white flex items-center justify-center p-2 relative group">
                        <img src={getMediaUrl(formData.appearance.logo_url)} alt="Main Logo" className="max-h-full max-w-full object-contain" />
                      </div>
                    )}
                    <div className="flex-1 flex gap-2">
                      <Input value={formData.appearance?.logo_url || ''} readOnly className="flex-1" placeholder="Logo bağlantısı" />
                      <Button type="button" variant="secondary" onClick={() => setPickerConfig({ isOpen: true, key: 'logo_url', title: 'Ana Logo Seç' })}>
                        Kütüphaneden Seç
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">Şeffaf arka planlı PNG tercih edilir.</p>
                </div>

                {/* Footer Logo Upload */}
                <div className="space-y-3">
                  <Label>Alt Kısım (Footer) Logosu</Label>
                  <div className="flex items-center gap-4">
                    {formData.appearance?.footer_logo_url && (
                      <div className="w-32 h-16 border rounded bg-slate-900 flex items-center justify-center p-2 relative group">
                        <img src={getMediaUrl(formData.appearance.footer_logo_url)} alt="Footer Logo" className="max-h-full max-w-full object-contain" />
                      </div>
                    )}
                    <div className="flex-1 flex gap-2">
                      <Input value={formData.appearance?.footer_logo_url || ''} readOnly className="flex-1" placeholder="Logo bağlantısı" />
                      <Button type="button" variant="secondary" onClick={() => setPickerConfig({ isOpen: true, key: 'footer_logo_url', title: 'Footer Logosu Seç' })}>
                        Kütüphaneden Seç
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">Koyu zemin üzerinde okunabilen açık renkli logo varyasyonu önerilir.</p>
                </div>

                {/* Favicon Upload */}
                <div className="space-y-3">
                  <Label>Favicon (Tarayıcı Sekme İkonu)</Label>
                  <div className="flex items-center gap-4">
                    {formData.appearance?.favicon_url && (
                      <div className="w-12 h-12 border rounded bg-white flex items-center justify-center p-1 relative group">
                        <img src={getMediaUrl(formData.appearance.favicon_url)} alt="Favicon" className="max-h-full max-w-full object-contain" />
                      </div>
                    )}
                    <div className="flex-1 flex gap-2">
                      <Input value={formData.appearance?.favicon_url || ''} readOnly className="flex-1" placeholder="Favicon bağlantısı" />
                      <Button type="button" variant="secondary" onClick={() => setPickerConfig({ isOpen: true, key: 'favicon_url', title: 'Favicon Seç' })}>
                        Kütüphaneden Seç
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">Kare formatta (32x32px veya 64x64px), .ico, .png, .svg formatları desteklenir.</p>
                </div>

              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Gelişmiş Görünüm (Özel CSS)</CardTitle>
                <CardDescription>Sitenize eklemek istediğiniz özel tasarım kodları (Dikkatli kullanın)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <Textarea
                  value={formData.custom_css || ''}
                  onChange={e => handleChange('custom_css', e.target.value)}
                  className="font-mono text-xs w-full min-h-[150px] bg-slate-950 text-slate-50 p-4"
                  placeholder="body { background-color: #f8f9fa; }"
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="eposta" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>E-posta Bildirim Ayarları</CardTitle>
                <CardDescription>Siteniz üzerinden gerçekleşen form gönderimlerinin yönlendirilmesini kontrol edin.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="flex items-center justify-between p-4 border rounded-md bg-muted/20">
                  <div className="space-y-0.5">
                    <Label className="text-base">Müşteri İletişim Formları Bildirimleri</Label>
                    <p className="text-sm text-muted-foreground">İletişim sayfasından gelen mesajlar yetkililere e-posta atılsın.</p>
                  </div>
                  <Switch checked={formData.email_settings?.contact_notifications ?? true} onCheckedChange={(val) => handleNestedChange('email_settings', 'contact_notifications', val)} />
                </div>

                <div className="flex items-center justify-between p-4 border rounded-md bg-muted/20">
                  <div className="space-y-0.5">
                    <Label className="text-base">İnsan Kaynakları Başvuruları Bildirimleri</Label>
                    <p className="text-sm text-muted-foreground">Kariyer modülünden yapılan iş başvuruları yetkililere e-posta atılsın.</p>
                  </div>
                  <Switch checked={formData.email_settings?.job_application_notifications ?? true} onCheckedChange={(val) => handleNestedChange('email_settings', 'job_application_notifications', val)} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>SMTP Sunucu Ayarları (Giden Posta)</CardTitle>
                <CardDescription>Sistemin bildirim e-postalarını gönderebilmesi için gereken sunucu bağlantı detayları</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="smtp-host">SMTP Sunucusu / Host</Label>
                    <Input id="smtp-host" placeholder="Örn: mail.skgmetal.com" value={formData.smtp_settings?.host || ''} onChange={e => handleNestedChange('smtp_settings', 'host', e.target.value)} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp-port">Port (587, 465 vb.)</Label>
                    <Input id="smtp-port" type="number" placeholder="587" value={formData.smtp_settings?.port || ''} onChange={e => handleNestedChange('smtp_settings', 'port', e.target.value)} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp-user">Kullanıcı Adı (E-posta)</Label>
                    <Input id="smtp-user" placeholder="noreply@skgmetal.com" value={formData.smtp_settings?.user || ''} onChange={e => handleNestedChange('smtp_settings', 'user', e.target.value)} className="h-9" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp-pass">Şifre</Label>
                    <Input id="smtp-pass" type="password" placeholder="••••••••" value={formData.smtp_settings?.pass || ''} onChange={e => handleNestedChange('smtp_settings', 'pass', e.target.value)} className="h-9" />
                  </div>
                </div>
                <div className="flex items-center space-x-2 pt-2">
                  <Switch id="smtp-tls" checked={formData.smtp_settings?.require_tls ?? true} onCheckedChange={(val) => handleNestedChange('smtp_settings', 'require_tls', val)} />
                  <Label htmlFor="smtp-tls">Güvenli Bağlantı (TLS/SSL) Zorunlu Kıl</Label>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="kvkk" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>KVKK ve Çerez Politikası</CardTitle>
                <CardDescription>Kullanıcı izni ve bilgilendirme uyarılarını yönetin</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-sm">
                <div className="flex items-center justify-between p-4 border rounded-md">
                  <div className="space-y-0.5">
                    <Label className="text-base text-primary">Kullanıcı Çerez Uyarı Pop-up'ı</Label>
                    <p className="text-sm text-muted-foreground">Siteye ilk kez giren kullanıcılara çerez onay çubuğu gösterilsin mi?</p>
                  </div>
                  <Switch checked={formData.cookie_settings?.enabled ?? false} onCheckedChange={(val) => handleNestedChange('cookie_settings', 'enabled', val)} />
                </div>

                <div className="space-y-2 mt-4">
                  <Label htmlFor="cookie-text">Çerez Uyarısı İçerik Metni</Label>
                  <Textarea
                    id="cookie-text"
                    disabled={!(formData.cookie_settings?.enabled)}
                    rows={4}
                    value={formData.cookie_settings?.text || ''}
                    onChange={e => handleNestedChange('cookie_settings', 'text', e.target.value)}
                    className="resize-none"
                    placeholder="Sitemizde daha iyi hizmet sunabilmek için çerezler kullanılmaktadır..."
                  />
                  <p className="text-xs text-muted-foreground">Bu metin kullanıcılara sunulacak çerez bildiriminde görünecektir.</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

        </Tabs>

        <MediaPicker
          open={pickerConfig.isOpen}
          onOpenChange={(val) => setPickerConfig(prev => ({ ...prev, isOpen: val }))}
          onSelect={(url) => {
            handleNestedChange('appearance', pickerConfig.key, url)
            setPickerConfig(prev => ({ ...prev, isOpen: false }))
          }}
          title={pickerConfig.title}
        />
      </Main>

      <AlertDialog open={confirmDialog?.isOpen} onOpenChange={(isOpen) => !isOpen && setConfirmDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDialog?.title}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog?.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUpdating}>İptal</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => {
              e.preventDefault(); // Prevent closing instantly, wait for async
              handleConfirmModeToggle();
            }} disabled={isUpdating}>
              {isUpdating ? 'Kaydediliyor...' : 'Evet, Onaylıyorum'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

export const Route = createFileRoute('/_authenticated/site-ayarlari/')({
  component: SiteAyalariPage,
})

